import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import DateInput from "../DateInput";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from "date-fns";
import {
    Alert,
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContentText,
    DialogTitle,
    Link
} from "@mui/material";
import useSWRMutation from "swr/mutation";
import { useClient } from "../../context/ClientProvider";
import { useUtils } from "../../context/UtilsProvider";
import { useAuth } from "../../context/AuthProvider";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import CloseIcon from "@mui/icons-material/Close";
import CallMeDialog from "./CallMeDialog";

const CallMeDialogLogin = ({ open, onClose, onCallbackSet }) => {
    const { useGetConfig } = useClient();
    const clientConfig = useGetConfig()?.clientConfig;
    const { createAlert, alertSeverityTypes } = useUtils();
    const { postIdvAuth, startSession } = useAuth();
    const { trigger, isMutating } = useSWRMutation("/auth/idv-no-postcode", postIdvAuth);

    const [showCallMeDialog, setShowCallMeDialog] = useState(false);
    const [localFormState, setLocalFormState] = useState({
        accountReference: "",
        dateOfBirth: "",
        organisation: ""
    });
    const [localFormError, setLocalFormError] = useState(null);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [persistedAccountReference, setPersistedAccountReference] = useState("");
    const [persistedDateOfBirth, setPersistedDateOfBirth] = useState("");
    const [callbackScheduled, setCallbackScheduled] = useState(false);
    const [requestedCallbackDatetime, setRequestedCallbackDatetime] = useState(null);

    console.log("Client organisation:", clientConfig?.name);

    useEffect(() => {
        if (open) {
            setLocalFormState({ accountReference: persistedAccountReference, dateOfBirth: persistedDateOfBirth });
            setLocalFormError(null);
        }
    }, [open, persistedAccountReference, persistedDateOfBirth]);

    useEffect(() => {
        const date = new Date(localFormState.dateOfBirth);
        const isValidDate = !isNaN(date);
        setButtonDisabled(!(localFormState.accountReference && isValidDate));
    }, [localFormState]);

    useEffect(() => {
        const lastCallback = localStorage.getItem("lastCallbackTime");
        if (lastCallback) {
            const callbackTime = new Date(lastCallback);
            const now = new Date();
            if (now < callbackTime) {
                setButtonDisabled(true);
            } else {
                localStorage.removeItem("lastCallbackTime");
                setButtonDisabled(false);
            }
        }
    }, [open]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setButtonDisabled(true);
        setLocalFormError(null);

        try {
            const date = new Date(localFormState.dateOfBirth);
            if (isNaN(date)) throw new Error("Invalid date");
            const formattedDate = format(date, "yyyy-MM-dd");

            const response = await trigger({
                accountReference: localFormState.accountReference,
                dateOfBirth: formattedDate,
                organisation: clientConfig?.name ?? "default-org",
            });

            if (response?.authenticationResult) {
                createAlert("Authentication successful", alertSeverityTypes.success);

                startSession({
                    accountReference: localFormState.accountReference,
                    dateOfBirth: formattedDate,
                });
                const dateOfBirth = localFormState.dateOfBirth;
                const accountRef = localFormState.accountReference;
                setPersistedAccountReference(accountRef);
                setPersistedDateOfBirth(dateOfBirth);
                setCallbackScheduled(!!response.callbackScheduled);
                setRequestedCallbackDatetime(response.requestedCallbackDatetime ?? null);

                if (response?.callbackScheduled && response?.requestedCallbackDatetime) {
                    localStorage.setItem("lastCallbackTime", response.requestedCallbackDatetime);
                }
                onClose();
                setShowCallMeDialog(true);
                onCallbackSet?.(localFormState.accountReference, true); // ✅ instantly show clock icon

            }
            else {
                throw new Error("Invalid credentials. Please try again.");
            }
        } catch (error) {
            console.error("Authentication failed", error);
            setLocalFormError(
                error.message.includes("Invalid date")
                    ? "Invalid date of birth. Please enter a valid date."
                    : (
                        <Typography>
                            Unable to verify your account. Contact us at{" "}
                            <Link href="tel:+01737237370" color="#fff">
                                01737 237 370
                            </Link>
                        </Typography>
                    )
            );

            createAlert(
                error.message.includes("Invalid date")
                    ? "Invalid date of birth. Please enter a valid date."
                    : "Unable to verify your account. Contact support.",
                alertSeverityTypes.error
            );
        } finally {
            setButtonDisabled(false);
        }
    };

    return (
        <>
            {clientConfig && (
                <Dialog
                    open={open}
                    onClose={onClose}
                    sx={{ padding: { xs: 0, md: 3 }, textAlign: "center", margin: "auto", maxWidth: "510px", width: "100%" }}
                >
                    <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon sx={{ fontSize: "2rem", color: { xs: "#fff", md: "#000" } }} />
                    </IconButton>
                    <PhoneInTalkIcon sx={{
                        width: { md: "100px", sm: "80px", xs: "60px" },
                        height: { md: "100px", sm: "80px", xs: "60px" },
                        ml: "auto", mr: "auto", mt: { md: 7, xs: 4, sm: 6 },
                        color: "primary.dark"
                    }} />
                    <DialogTitle variant="h4" sx={{ fontWeight: "bold", fontSize: { xs: "25px" } }}>
                        Call me back
                    </DialogTitle>
                    <DialogContentText sx={{
                        maxWidth: "90%", padding: 1, color: "#000", ml: "auto", mr: "auto", fontSize: { xs: "14px" }
                    }}>
                        We will endeavour to call you back as close to your requested time as possible, however during peak periods there may be a delay before we are able to call you back.
                    </DialogContentText>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                        <Typography variant="body1" sx={{
                            fontWeight: "bold", textAlign: "left", width: '100%', ml: { md: 3, sm: 2, xs: 1 }, fontSize: { xs: "14px" }
                        }}>
                            {clientConfig?.name} reference number:
                        </Typography>
                        <TextField
                            margin="dense"
                            required
                            id="accountReference"
                            label={`Enter your ${clientConfig?.name} reference number`}
                            name="accountReference"
                            autoFocus
                            sx={{ width: { xs: "100%", sm: "85%", md: "100%" }, pl: { xs: 0 } }}
                            onChange={(e) => setLocalFormState({ ...localFormState, accountReference: e.target.value })}
                            value={localFormState.accountReference}
                        />

                        <Typography variant="body1" sx={{
                            fontWeight: "bold", textAlign: "left", width: '100%', fontSize: { xs: "14px" }
                        }}>
                            Date of birth:
                        </Typography>
                        <DateInput
                            value={localFormState.dateOfBirth}
                            sx={{ width: { xs: "100%", sm: "85%", md: "100%" }, padding: { xs: 0 } }}
                            onChange={(value) => setLocalFormState({ ...localFormState, dateOfBirth: value })}
                        />

                        <DialogActions sx={{
                            display: "flex", flexDirection: "column", alignItems: 'center', width: '100%', m: { md: 2, xs: 0 }
                        }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: 'center', gap: 2, p: { xs: 2, md: 1 } }}>
                                {isMutating && <CircularProgress size={24} sx={{ mt: 2, mb: 2 }} />}
                                {localFormError && <Alert variant="filled" severity="error" sx={{ mt: 1, mb: 2 }}>{localFormError}</Alert>}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    onClick={handleSubmit}
                                    sx={{
                                        width: { xs: "80%", md: "110%" },
                                        backgroundColor: "primary.dark",
                                        "&:hover": {
                                            backgroundColor: "primary.light",
                                            color: "primary.dark",
                                        },
                                    }}
                                    disabled={buttonDisabled || isMutating}
                                >
                                    Continue
                                </Button>
                            </Box>
                        </DialogActions>
                    </Box>
                </Dialog>
            )}

            <CallMeDialog
                open={showCallMeDialog}
                onClose={() => setShowCallMeDialog(false)}
                clientRef={persistedAccountReference}
                dateOfBirth={persistedDateOfBirth}
                callbackScheduled={callbackScheduled}
                requestedCallbackDatetime={requestedCallbackDatetime}
            />

        </>
    );
};

CallMeDialogLogin.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCallbackSet: PropTypes.func.isRequired,

};

export default CallMeDialogLogin;
